import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BarChart3, Calendar, Clock3, Shield, Target, Trophy, TrendingUp, Users } from 'lucide-react';
import { useStore } from '../hooks/useStore';
import { buildPublicGameSnapshot, type PublicGameSnapshot } from '../services/liveGameService';
import { PublicEventSection } from '../components/PageTemplates';

interface LiveGameResponse {
  ok?: boolean;
  snapshot?: PublicGameSnapshot;
}

export function PublicGamePage() {
  const { gameId = '', slug = '' } = useParams<{ gameId: string; slug: string }>();
  const db = useStore((d) => d);
  const localSnapshot = buildPublicGameSnapshot(db, gameId);
  const [remoteSnapshot, setRemoteSnapshot] = useState<PublicGameSnapshot | null>(null);
  const snapshot = remoteSnapshot ?? localSnapshot;

  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;
    const load = async () => {
      try {
        const response = await fetch(`/api/live/games/${encodeURIComponent(gameId)}`);
        const data = (await response.json()) as LiveGameResponse;
        if (!cancelled && data.ok && data.snapshot) setRemoteSnapshot(data.snapshot);
      } catch {
        // Local store fallback keeps dev/test usable without Pages Functions.
      } finally {
        const status = remoteSnapshot?.game.status ?? localSnapshot?.game.status;
        const interval = status === 'in_progress' ? 5000 : status === 'scheduled' || status === 'ready' ? 30000 : 60000;
        if (!cancelled) timer = window.setTimeout(load, interval);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [gameId, localSnapshot?.game.status, remoteSnapshot?.game.status]);

  if (!snapshot) {
    return (
      <div className="empty">
        <Trophy size={22} />
        <p>Game not found.</p>
        <Link className="btn btn--ghost" to={`/events/${slug}`}><ArrowLeft size={14} /> Back to event</Link>
      </div>
    );
  }

  const isLive = snapshot.game.status === 'in_progress';
  const isFinal = snapshot.game.status === 'final' || snapshot.game.status === 'forfeit';

  return (
    <PublicEventSection>
    <div className="public-game">
      <Link className="btn btn--ghost btn--sm" to={`/events/${snapshot.tournament.slug}`}>
        <ArrowLeft size={14} /> Back to event
      </Link>

      <section className="public-game__hero">
        <div className="row row--between" style={{ gap: 12, flexWrap: 'wrap' }}>
          <span className={`chip ${isLive ? 'chip--ok' : isFinal ? 'chip--info' : 'chip--accent'}`}>
            <span className="chip__dot" /> {snapshot.game.status.replace('_', ' ')}
          </span>
          {snapshot.result && (
            <span className="chip">{formatDecision(snapshot.result.decidedBy)}</span>
          )}
          <span className="muted">Updated {new Date(snapshot.generatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
        </div>
        <div className="public-game__scoreline">
          <PublicTeamScore slug={snapshot.tournament.slug} team={snapshot.homeTeam} score={snapshot.score.home} />
          <div className="public-game__center">
            <span>vs</span>
            <strong>{snapshot.divisionName ?? snapshot.tournament.name}</strong>
          </div>
          <PublicTeamScore slug={snapshot.tournament.slug} team={snapshot.awayTeam} score={snapshot.score.away} align="right" />
        </div>
        <div className="public-game__meta">
          <span><Calendar size={15} /> {snapshot.startsAt ? new Date(snapshot.startsAt).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Time TBD'}</span>
          <span><Shield size={15} /> {snapshot.rinkName ?? 'Rink TBD'}</span>
          <span><Target size={15} /> Shots {snapshot.score.homeShots}-{snapshot.score.awayShots}</span>
        </div>
      </section>

      <div className="grid grid--2">
        <MomentumCard snapshot={snapshot} />
        <InsightsCard snapshot={snapshot} />
      </div>

      <div className="grid grid--2">
        <div className="card">
          <div className="card__head">
            <div>
              <h3 className="card__title">Game flow</h3>
              <p className="card__sub">Goals, penalties, and key scorer-table events.</p>
            </div>
          </div>
          {snapshot.timeline.length === 0 ? (
            <div className="empty">
              <Clock3 size={20} />
              {isLive ? 'This game is live. Events will appear here as the scorekeeper records them.' : 'No scorer-table events yet.'}
            </div>
          ) : (
            <div className="public-game__timeline">
              {snapshot.timeline.map((event) => (
                <div key={event.id} className="public-game__timeline-row">
                  <div>
                    <strong>{event.label}</strong>
                    <div className="muted">{event.teamName ?? 'Game'} {formatGameClock(event.period, event.gameSecond, snapshot.periodLengthMinutes)}</div>
                    {event.contextLabels && event.contextLabels.length > 0 && (
                      <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                        {event.contextLabels.map((label) => <span key={label} className="chip chip--info">{label}</span>)}
                      </div>
                    )}
                  </div>
                  <span className="chip">{event.type.replaceAll('_', ' ')}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card__head">
            <div>
              <h3 className="card__title">{isFinal ? 'Final summary' : 'Live totals'}</h3>
              <p className="card__sub">Official once the scorekeeper submits the game.</p>
            </div>
          </div>
          <div className="public-game__totals">
            <Metric label="Shots" home={snapshot.score.homeShots} away={snapshot.score.awayShots} />
            <Metric label="Penalty minutes" home={snapshot.score.homePims} away={snapshot.score.awayPims} />
            <Metric label="Goals" home={snapshot.score.home} away={snapshot.score.away} />
          </div>
          {snapshot.result && (
            <div className="note note--ok" style={{ marginTop: 14 }}>
              Official {formatDecision(snapshot.result.decidedBy).toLowerCase()} result submitted {new Date(snapshot.result.endedAt).toLocaleString()}.
            </div>
          )}
        </div>
      </div>

      {snapshot.shootout.attempts.length > 0 && <ShootoutCard snapshot={snapshot} />}

      <div className="grid grid--2">
        <PeriodBreakdownCard snapshot={snapshot} />
        <PlayerImpactCard snapshot={snapshot} />
      </div>
    </div>
    </PublicEventSection>
  );
}

function ShootoutCard({ snapshot }: { snapshot: PublicGameSnapshot }) {
  return (
    <div className="card">
      <div className="card__head">
        <div>
          <h3 className="card__title">Shootout</h3>
          <p className="card__sub">
            Shootout attempts decide the official result and are tracked separately from goals and points.
          </p>
        </div>
        <span className="chip">
          {snapshot.homeTeam.shortCode} {snapshot.shootout.homeGoals} - {snapshot.awayTeam.shortCode} {snapshot.shootout.awayGoals}
        </span>
      </div>
      <div className="table-scroll">
        <table className="table public-game__period-table">
          <thead>
            <tr>
              <th>Round</th>
              <th>Team</th>
              <th>Shooter</th>
              <th>Goalie</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {snapshot.shootout.attempts.map((attempt) => {
              const made = attempt.shootoutMade ?? attempt.label.includes('scored');
              return (
                <tr key={attempt.id}>
                  <td>{attempt.shootoutRound ? `R${attempt.shootoutRound}` : '-'}</td>
                  <td>{attempt.teamName ?? '-'}</td>
                  <td><strong>{attempt.playerName ?? 'Unknown shooter'}</strong></td>
                  <td>{attempt.goalieName ?? '-'}</td>
                  <td>
                    <span className={`chip ${made ? 'chip--ok' : 'chip--info'}`}>
                      {made ? 'Goal' : 'Save / miss'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PublicTeamScore({ slug, team, score, align }: { slug: string; team: PublicGameSnapshot['homeTeam']; score: number; align?: 'right' }) {
  return (
    <div className={`public-game__team ${align === 'right' ? 'is-right' : ''}`}>
      <span style={{ background: team.primaryColor }} />
      <div>
        <Link to={`/events/${slug}/teams/${team.id}`}><strong>{team.name}</strong></Link>
        <div className="muted">{team.shortCode}</div>
      </div>
      <b>{score}</b>
    </div>
  );
}

function Metric({ label, home, away }: { label: string; home: number; away: number }) {
  return (
    <div className="public-game__metric">
      <span>{label}</span>
      <strong>{home}</strong>
      <strong>{away}</strong>
    </div>
  );
}

function MomentumCard({ snapshot }: { snapshot: PublicGameSnapshot }) {
  const analytics = snapshot.analytics;
  return (
    <div className="card public-game__analytics-card">
      <div className="card__head">
        <div>
          <h3 className="card__title"><TrendingUp size={16} style={{ verticalAlign: -2, marginRight: 6 }} /> Momentum</h3>
          <p className="card__sub">Game flow from goals, shots, and penalties.</p>
        </div>
        <span className="chip">{analytics.edgeLabel}</span>
      </div>
      <div className="public-game__momentum-score">
        <div>
          <strong>{analytics.homeMomentum}</strong>
          <span>{snapshot.homeTeam.shortCode}</span>
        </div>
        <div>
          <strong>{analytics.awayMomentum}</strong>
          <span>{snapshot.awayTeam.shortCode}</span>
        </div>
      </div>
      <div className="public-game__momentum-bar" aria-label="Momentum balance">
        <span
          style={{
            width: `${analytics.homeMomentum}%`,
            background: snapshot.homeTeam.primaryColor,
          }}
        />
        <span
          style={{
            width: `${analytics.awayMomentum}%`,
            background: snapshot.awayTeam.primaryColor,
          }}
        />
      </div>
      <MomentumChart snapshot={snapshot} />
    </div>
  );
}

function MomentumChart({ snapshot }: { snapshot: PublicGameSnapshot }) {
  const points = snapshot.analytics.momentumTimeline;
  if (points.length < 2) {
    return (
      <div className="public-game__chart-empty">
        Record goals, shots, or penalties to build the live momentum chart.
      </div>
    );
  }
  const pointString = points
    .map((point, index) => {
      const x = points.length === 1 ? 0 : (index / (points.length - 1)) * 100;
      const y = 100 - point.homeMomentum;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
  return (
    <svg className="public-game__momentum-chart" viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label="Momentum timeline">
      <line x1="0" y1="50" x2="100" y2="50" className="public-game__momentum-midline" />
      <polyline points={pointString} className="public-game__momentum-line" />
      {points
        .filter((point) => point.type === 'goal' || point.type === 'penalty_shot_made')
        .map((point) => {
          const index = points.findIndex((item) => item.id === point.id);
          const x = points.length === 1 ? 0 : (index / (points.length - 1)) * 100;
          return (
            <circle
              key={point.id}
              cx={x}
              cy={100 - point.homeMomentum}
              r="2.5"
              className="public-game__momentum-dot"
            />
          );
        })}
    </svg>
  );
}

function InsightsCard({ snapshot }: { snapshot: PublicGameSnapshot }) {
  return (
    <div className="card public-game__analytics-card">
      <div className="card__head">
        <div>
          <h3 className="card__title"><BarChart3 size={16} style={{ verticalAlign: -2, marginRight: 6 }} /> Game intelligence</h3>
          <p className="card__sub">Live insights that remain after the final horn.</p>
        </div>
      </div>
      <div className="public-game__insights">
        {snapshot.analytics.keyInsights.map((insight) => (
          <div key={`${insight.label}-${insight.value}`} className={`public-game__insight public-game__insight--${insight.tone}`}>
            <span>{insight.label}</span>
            <strong>{insight.value}</strong>
            <p>{insight.detail}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PeriodBreakdownCard({ snapshot }: { snapshot: PublicGameSnapshot }) {
  return (
    <div className="card">
      <div className="card__head">
        <div>
          <h3 className="card__title"><BarChart3 size={16} style={{ verticalAlign: -2, marginRight: 6 }} /> Period breakdown</h3>
          <p className="card__sub">Goals, tracked shots, and penalty minutes by period.</p>
        </div>
      </div>
      <div className="table-scroll">
        <table className="table public-game__period-table">
          <thead>
            <tr>
              <th>Period</th>
              <th>{snapshot.homeTeam.shortCode} G</th>
              <th>{snapshot.awayTeam.shortCode} G</th>
              <th>Shots</th>
              <th>PIM</th>
            </tr>
          </thead>
          <tbody>
            {snapshot.analytics.periodBreakdown.map((row) => (
              <tr key={row.period}>
                <td><strong>{row.label}</strong></td>
                <td>{row.homeGoals}</td>
                <td>{row.awayGoals}</td>
                <td>{row.homeShots}-{row.awayShots}</td>
                <td>{row.homePims}-{row.awayPims}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlayerImpactCard({ snapshot }: { snapshot: PublicGameSnapshot }) {
  return (
    <div className="card">
      <div className="card__head">
        <div>
          <h3 className="card__title"><Users size={16} style={{ verticalAlign: -2, marginRight: 6 }} /> Player impact</h3>
          <p className="card__sub">A game-only impact view from scoring events.</p>
        </div>
      </div>
      {snapshot.analytics.playerImpact.length === 0 ? (
        <div className="empty">Player impact appears once goals, assists, shots, or penalties are recorded.</div>
      ) : (
        <div className="public-game__impact-list">
          {snapshot.analytics.playerImpact.map((player, index) => (
            <div key={player.playerId} className="public-game__impact-row">
              <span className="public-game__impact-rank">{index + 1}</span>
              <div>
                <Link to={`/events/${snapshot.tournament.slug}/players/${player.playerId}`}><strong>{player.playerName}</strong></Link>
                <div className="muted">
                  {player.teamId ? <Link to={`/events/${snapshot.tournament.slug}/teams/${player.teamId}`}>{player.teamName ?? 'Game'}</Link> : player.teamName ?? 'Game'} - {player.goals}G {player.assists}A {player.shots}S {player.penaltyMinutes}PIM
                  {player.shootoutAttempts > 0 ? ` - SO ${player.shootoutGoals}/${player.shootoutAttempts}` : ''}
                </div>
                {player.labels.length > 0 && (
                  <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    {player.labels.map((label) => <span key={label} className="chip chip--info">{label}</span>)}
                  </div>
                )}
              </div>
              <strong className="public-game__impact-score">{Math.round(player.impactScore)}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDecision(decidedBy: NonNullable<PublicGameSnapshot['result']>['decidedBy']): string {
  if (decidedBy === 'shootout') return 'Shootout';
  if (decidedBy === 'overtime') return 'Overtime';
  if (decidedBy === 'forfeit') return 'Forfeit';
  return 'Regulation';
}

function formatGameClock(period?: number, gameSecond?: number, periodLengthMinutes = 20): string {
  if (!period && gameSecond === undefined) return '';
  const periodLengthSeconds = Math.max(60, Math.floor(periodLengthMinutes * 60));
  const withinPeriod = gameSecond === undefined ? undefined : gameSecond % periodLengthSeconds;
  const minute = withinPeriod === undefined ? null : Math.floor(withinPeriod / 60);
  const second = withinPeriod === undefined ? null : withinPeriod % 60;
  return `P${period ?? '?'}${minute === null ? '' : ` ${minute}:${String(second).padStart(2, '0')}`}`;
}
